import { FormControl, Select } from '@mui/material'
import debounce from 'lodash.debounce'

import { useCallback, useState } from 'react'

import {
  Flex,
  FloraInput,
  DrawerTrigger,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  FloraButton,
  Box,
  FloraTypography
} from '@grupoboticario/flora-react'

import ActionDrawer from '@/components/Drawer/ActionDrawer'
import { ClearFiltersIcon } from '@/icons/ClearFilters'
import { SettingsIcon } from '@/icons/Settings'

import * as S from './Filter.styles'

const Filter = ({
  placeholder = '',
  subtitle = '',
  type = 'text',
  content = [],
  state = {},
  defaultKey = 'search',
  onChange = () => {},
  ...props
}) => {
  const [filterState, setFilterState] = useState({})
  const [filterCountState, setFilterCountState] = useState(0)

  const debouncedSearchEvent = debounce(
    (defaultKey, defaultFilterValue) => updateInstantly(defaultKey, defaultFilterValue),
    500
  )

  // Updates the state bypassing the internal state (will instantly call submit function)
  const updateInstantly = (filterKey, filterValue) => {
    const newState = { ...state }
    newState[filterKey] = filterValue
    submit(newState)
  }

  // Updates the internal filter state, for when the user apply, send it with the submit function
  const update = (filterKey, filterValue) => {
    const newFilterState = { ...state, ...filterState }
    newFilterState[filterKey] = filterValue
    setFilterState(newFilterState)
  }

  // Submits the value for the onChange callback and recalculate the filter counter
  const submit = (value) => {
    onChange(value)
    setFilterCountState(getFiltersCount(value))
  }

  // Resets the state, sending a empty object for the submit func, but keeping the general search state
  const reset = () => {
    const resetedValue = {}
    resetedValue[defaultKey] = state[defaultKey] || ''
    submit(resetedValue)
  }

  // Will get the state, extract all keys, handle date-range doubled values,
  // remove the general search key and return the number of applied filters
  const getFiltersCount = useCallback(
    (filterStateParam) =>
      new Set(
        Object.keys(filterStateParam)
          .filter((filterKey) => Boolean(filterStateParam[filterKey]))
          .map((filterKey) => filterKey.replaceAll('-from', '').replaceAll('-to', '').replaceAll(defaultKey, ''))
          .filter(Boolean)
      ).size,
    [defaultKey]
  )

  return (
    <Flex gap="$3" {...props}>
      <FloraInput
        id="general-search"
        label={placeholder}
        onChange={(e) => debouncedSearchEvent(defaultKey, e.target.value)}
        size="medium"
        type={type}
        data-testid="general-search-input"
      />
      <ActionDrawer
        buttonTrigger={
          <DrawerTrigger>
            <FloraButton has="iconLeft" icon={<SettingsIcon />}>
              Filtros
              {filterCountState > 0 && (
                <Flex align="center" css={S.filterBadgeCss} justify="center">
                  <FloraTypography fontSize="exceptionsAuxiliar" css={S.filterBadgeTextCss}>
                    {filterCountState}
                  </FloraTypography>
                </Flex>
              )}
            </FloraButton>
          </DrawerTrigger>
        }
        onOpenChange={() => setFilterState(state)}
      >
        <DrawerHeader css={S.drawerHeaderCss}>
          <Flex direction="column">
            <FloraTypography fontSize="bodyLargeStandard" fontWeight="bold">
              Filtros
            </FloraTypography>
            {subtitle && <FloraTypography fontSize="exceptionsAuxiliar">{subtitle}</FloraTypography>}
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          {content?.map((category) => (
            <Box css={S.categoryBoxCss} key={category.key}>
              <FloraTypography fontSize="bodyLargeStandard">{category.title}</FloraTypography>
              {category.filters?.map((filter) => (
                <Box css={S.filterBoxCss} key={filter.key}>
                  {filter.type === 'select' && (
                    <FormControl style={{ width: '100%' }} variant="outlined">
                      <Select
                        defaultValue={state[filter.key]}
                        id={filter.key}
                        native
                        onChange={(e) =>
                          update(filter.key, e.target.value.includes(',') ? e.target.value.split(',') : e.target.value)
                        }
                        style={{ height: '48px' }}
                      >
                        {filter.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {(filter.type === 'text' || filter.type === 'number') && (
                    <FloraInput
                      aria-label={filter.label}
                      defaultValue={state[filter.key]}
                      id={filter.key}
                      label={filter.label}
                      onChange={(e) => update(filter.key, e.target.value)}
                      size="medium"
                      type={filter.type}
                    />
                  )}
                  {filter.type === 'date-range' && (
                    <Flex gap="$2">
                      <Box css={S.dateRangeHalfCss}>
                        <FloraInput
                          aria-label="De"
                          defaultValue={state[`${filter.key}-from`]}
                          id={`date-${filter.key}-from`}
                          label="De"
                          onChange={(e) => update(`${filter.key}-from`, e.target.value)}
                          size="medium"
                          type="date"
                        />
                      </Box>
                      <Box css={S.dateRangeHalfCss}>
                        <FloraInput
                          aria-label="Até"
                          defaultValue={state[`${filter.key}-to`]}
                          id={`date-${filter.key}-to`}
                          status={
                            filterState[`${filter.key}-from`] && !filterState[`${filter.key}-to`] ? 'error' : 'neutral'
                          }
                          helperText={
                            filterState[`${filter.key}-from`] && !filterState[`${filter.key}-to`]
                              ? 'Digite uma data limite'
                              : undefined
                          }
                          label="Até"
                          onChange={(e) => update(`${filter.key}-to`, e.target.value)}
                          size="medium"
                          type="date"
                        />
                      </Box>
                    </Flex>
                  )}
                  {filter.type === 'date' && (
                    <FloraInput
                      aria-label="Selecione uma data"
                      defaultValue={state[`${filter.key}`]}
                      id={`date-${filter.key}`}
                      label="Selecione uma data"
                      onChange={(e) => update(`${filter.key}`, e.target.value)}
                      size="medium"
                      type="date"
                    />
                  )}
                </Box>
              ))}
            </Box>
          ))}
          <Flex css={S.filterFooterCss} gap="$4" justify="flex-end">
            <DrawerCloseButton asChild>
              <FloraButton onClick={() => reset()} hierarchy="secondary" has="iconLeft" icon={<ClearFiltersIcon />}>
                Limpar Filtros
              </FloraButton>
            </DrawerCloseButton>
            <DrawerCloseButton asChild>
              <FloraButton onClick={() => submit(filterState)} has="iconLeft" icon={<SettingsIcon />}>
                Aplicar Filtros
              </FloraButton>
            </DrawerCloseButton>
          </Flex>
        </DrawerBody>
      </ActionDrawer>
    </Flex>
  )
}

export default Filter
